import { HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, PRIMARY_OUTLET, Router } from '@angular/router';
import {
  FeatureLoaderConfig,
  LoaderPolicyRule,
  ResolvedLoaderPolicy,
  RouteLoaderPolicyData,
} from '../models/loader-policy.models';
import {
  LoaderOwner,
  LoaderSource,
  LoaderStartConfig,
} from '../models/loading.models';
import {
  LOADER_GROUP_CONTEXT,
  LOADER_MESSAGE_CONTEXT,
  LOADER_MODE_CONTEXT,
  LOADER_MODULE_KEY_CONTEXT,
  LOADER_PRIORITY_CONTEXT,
  LOADER_REFERENCE_CONTEXT,
  LOADER_ROUTE_KEY_CONTEXT,
  LOADER_SCOPE_CONTEXT,
  LOADER_SKIP_CONTEXT,
} from '../tokens/http-loader-context.token';
import { resolveLoaderStartConfig } from '../utils/loading-defaults.util';

interface LoaderPolicyResolutionContext {
  activeRouteKey: string;
  activeModuleKey?: string;
  activeRouteLoaderPolicy?: RouteLoaderPolicyData;
  comparableRequestUrl: string;
  contextFlags: Set<string>;
  explicitRequestOverrides: Partial<LoaderStartConfig>;
  manualLoaderStartConfig?: Partial<LoaderStartConfig>;
  requestMethod: string;
  skipRequested: boolean;
}

interface RoutePolicyResolutionOptions {
  owner?: LoaderOwner;
  routeKey?: string;
  routeLoaderPolicy?: RouteLoaderPolicyData;
  source?: Extract<LoaderSource, 'route' | 'resolver'>;
}

@Injectable({
  providedIn: 'root',
})
export class LoaderPolicyService {
  private readonly featureLoaderConfigsByModuleKey = new Map<string, FeatureLoaderConfig>();
  private readonly loaderPolicyRulesById = new Map<string, LoaderPolicyRule>();

  constructor(private readonly router: Router) {}

  registerFeatureLoaderConfig(featureLoaderConfig: FeatureLoaderConfig): void {
    this.featureLoaderConfigsByModuleKey.set(
      featureLoaderConfig.moduleKey,
      featureLoaderConfig
    );
  }

  registerLoaderPolicyRule(loaderPolicyRule: LoaderPolicyRule): void {
    this.loaderPolicyRulesById.set(loaderPolicyRule.id, loaderPolicyRule);
  }

  resolveHttpPolicy(
    httpRequest: HttpRequest<unknown>,
    manualLoaderStartConfig?: Partial<LoaderStartConfig>
  ): ResolvedLoaderPolicy {
    const explicitRequestOverrides = this.readExplicitRequestOverrides(httpRequest);
    const activeRouteLoaderPolicy = this.getActiveRouteLoaderPolicy();
    const activeRouteKey =
      explicitRequestOverrides.routeKey ??
      manualLoaderStartConfig?.routeKey ??
      activeRouteLoaderPolicy?.routeKey ??
      this.normalizeRouteKey(this.router.url || '/');
    const activeFeatureLoaderConfig = this.resolveActiveFeatureLoaderConfig(
      explicitRequestOverrides.moduleKey ??
        manualLoaderStartConfig?.moduleKey ??
        activeRouteLoaderPolicy?.moduleKey,
      activeRouteKey
    );
    const activeModuleKey =
      explicitRequestOverrides.moduleKey ??
      manualLoaderStartConfig?.moduleKey ??
      activeRouteLoaderPolicy?.moduleKey ??
      activeFeatureLoaderConfig?.moduleKey;
    const comparableRequestUrl = this.getComparableRequestUrl(httpRequest.url);
    const resolutionContext: LoaderPolicyResolutionContext = {
      activeRouteKey,
      activeModuleKey,
      activeRouteLoaderPolicy,
      comparableRequestUrl,
      contextFlags: this.buildContextFlags(httpRequest, explicitRequestOverrides),
      explicitRequestOverrides,
      manualLoaderStartConfig,
      requestMethod: httpRequest.method.toUpperCase(),
      skipRequested: httpRequest.context.get(LOADER_SKIP_CONTEXT),
    };

    if (resolutionContext.skipRequested) {
      return this.buildSkippedPolicy(
        this.buildGlobalHttpLoaderStartConfig(resolutionContext)
      );
    }

    const hasExplicitLoaderOverride =
      this.hasLoaderBehaviorOverride(explicitRequestOverrides) ||
      this.hasLoaderBehaviorOverride(manualLoaderStartConfig);

    if (!this.isApiRequest(comparableRequestUrl) && !hasExplicitLoaderOverride) {
      return this.buildSkippedPolicy(
        this.buildGlobalHttpLoaderStartConfig(resolutionContext)
      );
    }

    let skip = false;
    let mergedLoaderStartConfig: Partial<LoaderStartConfig> =
      this.buildGlobalHttpLoaderStartConfig(resolutionContext);
    const matchingUrlPolicyRules = this.getMatchingUrlPolicyRules(
      resolutionContext,
      activeFeatureLoaderConfig
    );
    const matchingModulePolicyRules = this.getMatchingModulePolicyRules(
      resolutionContext,
      activeFeatureLoaderConfig
    );
    const matchingRoutePolicyRules = this.getMatchingRoutePolicyRules(
      resolutionContext,
      activeFeatureLoaderConfig
    );

    mergedLoaderStartConfig = this.mergeLoaderStartConfig(
      mergedLoaderStartConfig,
      this.resolveRulesForLayer(matchingUrlPolicyRules)
    );
    skip = this.resolveSkipFlag(skip, matchingUrlPolicyRules);

    mergedLoaderStartConfig = this.mergeLoaderStartConfig(
      mergedLoaderStartConfig,
      this.buildModuleLevelLoaderStartConfig(activeFeatureLoaderConfig)
    );
    mergedLoaderStartConfig = this.mergeLoaderStartConfig(
      mergedLoaderStartConfig,
      this.resolveRulesForLayer(matchingModulePolicyRules)
    );
    skip = this.resolveSkipFlag(skip, matchingModulePolicyRules);

    mergedLoaderStartConfig = this.mergeLoaderStartConfig(
      mergedLoaderStartConfig,
      this.buildRouteLevelLoaderStartConfig(activeFeatureLoaderConfig, activeRouteKey)
    );
    mergedLoaderStartConfig = this.mergeLoaderStartConfig(
      mergedLoaderStartConfig,
      this.resolveRulesForLayer(matchingRoutePolicyRules)
    );
    skip = this.resolveSkipFlag(skip, matchingRoutePolicyRules);

    if (activeRouteLoaderPolicy) {
      const { skip: routeSkip, ...routeLoaderStartConfig } = activeRouteLoaderPolicy;
      mergedLoaderStartConfig = this.mergeLoaderStartConfig(
        mergedLoaderStartConfig,
        routeLoaderStartConfig
      );

      if (routeSkip !== undefined) {
        skip = routeSkip;
      }
    }

    mergedLoaderStartConfig = this.mergeLoaderStartConfig(
      mergedLoaderStartConfig,
      manualLoaderStartConfig
    );

    if (this.hasLoaderBehaviorOverride(manualLoaderStartConfig)) {
      skip = false;
    }

    mergedLoaderStartConfig = this.mergeLoaderStartConfig(
      mergedLoaderStartConfig,
      explicitRequestOverrides
    );

    if (this.hasLoaderBehaviorOverride(explicitRequestOverrides)) {
      skip = false;
    }

    return this.buildResolvedPolicy(mergedLoaderStartConfig, skip);
  }

  resolveRoutePolicy(
    routeSnapshot?: ActivatedRouteSnapshot | null,
    routePolicyResolutionOptions?: RoutePolicyResolutionOptions
  ): ResolvedLoaderPolicy {
    const activeRouteLoaderPolicy =
      routePolicyResolutionOptions?.routeLoaderPolicy ??
      this.extractRouteLoaderPolicy(routeSnapshot);
    const activeRouteKey = this.normalizeRouteKey(
      routePolicyResolutionOptions?.routeKey ??
        activeRouteLoaderPolicy?.routeKey ??
        this.resolveRouteSnapshotKey(routeSnapshot) ??
        (this.router.url || '/')
    );
    const activeFeatureLoaderConfig = this.resolveActiveFeatureLoaderConfig(
      activeRouteLoaderPolicy?.moduleKey,
      activeRouteKey
    );
    const activeModuleKey =
      activeRouteLoaderPolicy?.moduleKey ?? activeFeatureLoaderConfig?.moduleKey;
    const resolutionContext: LoaderPolicyResolutionContext = {
      activeRouteKey,
      activeModuleKey,
      activeRouteLoaderPolicy,
      comparableRequestUrl: '',
      contextFlags: new Set<string>(),
      explicitRequestOverrides: {},
      requestMethod: 'GET',
      skipRequested: Boolean(activeRouteLoaderPolicy?.skip),
    };

    let skip = false;
    let mergedLoaderStartConfig: Partial<LoaderStartConfig> =
      this.buildGlobalRouteLoaderStartConfig(
        activeRouteKey,
        activeModuleKey,
        routePolicyResolutionOptions?.owner,
        routePolicyResolutionOptions?.source
      );
    const matchingModulePolicyRules = this.getMatchingModulePolicyRules(
      resolutionContext,
      activeFeatureLoaderConfig
    );
    const matchingRoutePolicyRules = this.getMatchingRoutePolicyRules(
      resolutionContext,
      activeFeatureLoaderConfig
    );

    mergedLoaderStartConfig = this.mergeLoaderStartConfig(
      mergedLoaderStartConfig,
      this.buildModuleLevelLoaderStartConfig(activeFeatureLoaderConfig)
    );
    mergedLoaderStartConfig = this.mergeLoaderStartConfig(
      mergedLoaderStartConfig,
      this.resolveRulesForLayer(matchingModulePolicyRules)
    );
    skip = this.resolveSkipFlag(skip, matchingModulePolicyRules);

    mergedLoaderStartConfig = this.mergeLoaderStartConfig(
      mergedLoaderStartConfig,
      this.buildRouteLevelLoaderStartConfig(activeFeatureLoaderConfig, activeRouteKey)
    );
    mergedLoaderStartConfig = this.mergeLoaderStartConfig(
      mergedLoaderStartConfig,
      this.resolveRulesForLayer(matchingRoutePolicyRules)
    );
    skip = this.resolveSkipFlag(skip, matchingRoutePolicyRules);

    if (activeRouteLoaderPolicy) {
      const { skip: routeSkip, ...routeLoaderStartConfig } = activeRouteLoaderPolicy;
      mergedLoaderStartConfig = this.mergeLoaderStartConfig(
        mergedLoaderStartConfig,
        routeLoaderStartConfig
      );

      if (routeSkip !== undefined) {
        skip = routeSkip;
      }
    }

    return this.buildResolvedPolicy(mergedLoaderStartConfig, skip);
  }

  extractRouteLoaderPolicy(
    routeSnapshot?: ActivatedRouteSnapshot | null
  ): RouteLoaderPolicyData | undefined {
    if (!routeSnapshot) {
      return undefined;
    }

    return this.getPrimaryRouteChain(routeSnapshot).reduce<RouteLoaderPolicyData | undefined>(
      (mergedRouteLoaderPolicy, activeRouteSnapshot) => {
        const routeLoaderPolicy = activeRouteSnapshot.data?.['loaderPolicy'] as
          | RouteLoaderPolicyData
          | undefined;

        if (!routeLoaderPolicy) {
          return mergedRouteLoaderPolicy;
        }

        return {
          ...(mergedRouteLoaderPolicy ?? {}),
          ...routeLoaderPolicy,
          scope: routeLoaderPolicy.scope ?? mergedRouteLoaderPolicy?.scope,
        };
      },
      undefined
    );
  }

  private buildResolvedPolicy(
    loaderStartConfig: Partial<LoaderStartConfig>,
    skip: boolean
  ): ResolvedLoaderPolicy {
    const finalizedLoaderStartConfig = resolveLoaderStartConfig(
      loaderStartConfig as LoaderStartConfig
    );

    return {
      skip,
      mode: finalizedLoaderStartConfig.mode,
      scope: finalizedLoaderStartConfig.scope,
      owner: finalizedLoaderStartConfig.owner,
      delayMs: finalizedLoaderStartConfig.delayMs ?? 0,
      minVisibleMs: finalizedLoaderStartConfig.minVisibleMs ?? 0,
      priority: finalizedLoaderStartConfig.priority ?? 0,
      groupId: finalizedLoaderStartConfig.groupId,
      blocking: finalizedLoaderStartConfig.blocking,
      message: finalizedLoaderStartConfig.message,
      referenceKey: finalizedLoaderStartConfig.referenceKey,
      routeKey: finalizedLoaderStartConfig.routeKey,
      moduleKey: finalizedLoaderStartConfig.moduleKey,
      source: finalizedLoaderStartConfig.source,
      metadata: finalizedLoaderStartConfig.metadata,
    };
  }

  private buildGlobalHttpLoaderStartConfig(
    resolutionContext: LoaderPolicyResolutionContext
  ): LoaderStartConfig {
    return {
      mode: 'top-bar',
      scope: { type: 'global', key: 'app' },
      owner: { type: 'http-interceptor', name: 'HttpLoaderInterceptor' },
      routeKey: resolutionContext.activeRouteKey,
      moduleKey: resolutionContext.activeModuleKey,
      source: 'http',
    };
  }

  private buildGlobalRouteLoaderStartConfig(
    routeKey: string,
    moduleKey: string | undefined,
    owner?: LoaderOwner,
    source: Extract<LoaderSource, 'route' | 'resolver'> = 'route'
  ): LoaderStartConfig {
    return {
      mode: 'top-bar',
      scope: { type: 'route', key: routeKey },
      owner: owner ?? { type: 'system', name: 'RouteLoaderService' },
      routeKey,
      moduleKey,
      source,
    };
  }

  private buildModuleLevelLoaderStartConfig(
    featureLoaderConfig?: FeatureLoaderConfig
  ): Partial<LoaderStartConfig> | undefined {
    if (!featureLoaderConfig) {
      return undefined;
    }

    const moduleLevelLoaderStartConfig: Partial<LoaderStartConfig> = {
      moduleKey: featureLoaderConfig.moduleKey,
    };

    if (featureLoaderConfig.defaultMode) {
      moduleLevelLoaderStartConfig.mode = featureLoaderConfig.defaultMode;
    }

    if (featureLoaderConfig.defaultScope) {
      moduleLevelLoaderStartConfig.scope = featureLoaderConfig.defaultScope;
    }

    return moduleLevelLoaderStartConfig;
  }

  private buildRouteLevelLoaderStartConfig(
    featureLoaderConfig: FeatureLoaderConfig | undefined,
    activeRouteKey: string
  ): Partial<LoaderStartConfig> | undefined {
    if (!featureLoaderConfig?.routeDefaults) {
      return undefined;
    }

    const configuredRouteKey = featureLoaderConfig.routeDefaults.routeKey;

    if (!configuredRouteKey || !this.matchesRoutePattern(activeRouteKey, configuredRouteKey)) {
      return undefined;
    }

    return featureLoaderConfig.routeDefaults;
  }

  private getMatchingRoutePolicyRules(
    resolutionContext: LoaderPolicyResolutionContext,
    featureLoaderConfig?: FeatureLoaderConfig
  ): LoaderPolicyRule[] {
    return this.getCandidatePolicyRules(featureLoaderConfig).filter(
      (loaderPolicyRule) =>
        Boolean(loaderPolicyRule.match.routePattern) &&
        this.matchesPolicyRule(loaderPolicyRule, resolutionContext)
    );
  }

  private getMatchingUrlPolicyRules(
    resolutionContext: LoaderPolicyResolutionContext,
    featureLoaderConfig?: FeatureLoaderConfig
  ): LoaderPolicyRule[] {
    return this.getCandidatePolicyRules(featureLoaderConfig).filter(
      (loaderPolicyRule) =>
        !loaderPolicyRule.match.routePattern &&
        !loaderPolicyRule.match.moduleKey &&
        this.matchesPolicyRule(loaderPolicyRule, resolutionContext)
    );
  }

  private getMatchingModulePolicyRules(
    resolutionContext: LoaderPolicyResolutionContext,
    featureLoaderConfig?: FeatureLoaderConfig
  ): LoaderPolicyRule[] {
    return this.getCandidatePolicyRules(featureLoaderConfig).filter(
      (loaderPolicyRule) =>
        !loaderPolicyRule.match.routePattern &&
        Boolean(loaderPolicyRule.match.moduleKey) &&
        this.matchesPolicyRule(loaderPolicyRule, resolutionContext)
    );
  }

  private getCandidatePolicyRules(
    featureLoaderConfig?: FeatureLoaderConfig
  ): LoaderPolicyRule[] {
    return [
      ...this.loaderPolicyRulesById.values(),
      ...(featureLoaderConfig?.requestRules ?? []),
    ].filter((loaderPolicyRule) => loaderPolicyRule.enabled);
  }

  private resolveRulesForLayer(
    loaderPolicyRules: LoaderPolicyRule[]
  ): Partial<LoaderStartConfig> | undefined {
    return loaderPolicyRules
      .sort((firstRule, secondRule) => firstRule.priority - secondRule.priority)
      .reduce<Partial<LoaderStartConfig>>((mergedLoaderStartConfig, loaderPolicyRule) => {
        const { skip: skippedByRule, ...resolvedLoaderStartConfig } =
          loaderPolicyRule.resolve;

        void skippedByRule;

        return this.mergeLoaderStartConfig(
          mergedLoaderStartConfig,
          resolvedLoaderStartConfig
        );
      }, {});
  }

  private resolveSkipFlag(
    currentSkipValue: boolean,
    loaderPolicyRules: LoaderPolicyRule[]
  ): boolean {
    return loaderPolicyRules
      .sort((firstRule, secondRule) => firstRule.priority - secondRule.priority)
      .reduce<boolean>((resolvedSkipValue, loaderPolicyRule) => {
        if (loaderPolicyRule.resolve.skip === undefined) {
          return resolvedSkipValue;
        }

        return loaderPolicyRule.resolve.skip;
      }, currentSkipValue);
  }

  private matchesPolicyRule(
    loaderPolicyRule: LoaderPolicyRule,
    resolutionContext: LoaderPolicyResolutionContext
  ): boolean {
    const { match } = loaderPolicyRule;

    if (
      match.routePattern &&
      !this.matchesRoutePattern(resolutionContext.activeRouteKey, match.routePattern)
    ) {
      return false;
    }

    if (match.moduleKey && match.moduleKey !== resolutionContext.activeModuleKey) {
      return false;
    }

    if (
      match.requestUrlStartsWith &&
      !this.matchesRequestUrlStartsWith(
        resolutionContext.comparableRequestUrl,
        match.requestUrlStartsWith
      )
    ) {
      return false;
    }

    if (
      match.requestUrlIncludes &&
      !resolutionContext.comparableRequestUrl.includes(match.requestUrlIncludes)
    ) {
      return false;
    }

    if (
      match.requestMethod &&
      match.requestMethod !== resolutionContext.requestMethod
    ) {
      return false;
    }

    if (
      match.contextFlag &&
      !resolutionContext.contextFlags.has(match.contextFlag)
    ) {
      return false;
    }

    return true;
  }

  private mergeLoaderStartConfig(
    baseLoaderStartConfig: Partial<LoaderStartConfig> | undefined,
    patchLoaderStartConfig: Partial<LoaderStartConfig> | undefined
  ): Partial<LoaderStartConfig> {
    return {
      ...(baseLoaderStartConfig ?? {}),
      ...(patchLoaderStartConfig ?? {}),
      owner: patchLoaderStartConfig?.owner ?? baseLoaderStartConfig?.owner,
      scope: patchLoaderStartConfig?.scope ?? baseLoaderStartConfig?.scope,
      metadata: {
        ...(baseLoaderStartConfig?.metadata ?? {}),
        ...(patchLoaderStartConfig?.metadata ?? {}),
      },
    };
  }

  private resolveActiveFeatureLoaderConfig(
    explicitModuleKey: string | undefined,
    activeRouteKey: string
  ): FeatureLoaderConfig | undefined {
    const availableFeatureLoaderConfigs = [...this.featureLoaderConfigsByModuleKey.values()];

    if (explicitModuleKey) {
      return availableFeatureLoaderConfigs.find(
        (featureLoaderConfig) => featureLoaderConfig.moduleKey === explicitModuleKey
      );
    }

    return [...availableFeatureLoaderConfigs]
      .sort((firstConfig, secondConfig) => {
        const firstRouteKeyLength = firstConfig.routeDefaults?.routeKey?.length ?? 0;
        const secondRouteKeyLength = secondConfig.routeDefaults?.routeKey?.length ?? 0;

        return secondRouteKeyLength - firstRouteKeyLength;
      })
      .find((featureLoaderConfig) => {
        const configuredRouteKey = featureLoaderConfig.routeDefaults?.routeKey;

        if (configuredRouteKey) {
          return this.matchesRoutePattern(activeRouteKey, configuredRouteKey);
        }

        return activeRouteKey.startsWith(`/${featureLoaderConfig.moduleKey}`);
      });
  }

  private readExplicitRequestOverrides(
    httpRequest: HttpRequest<unknown>
  ): Partial<LoaderStartConfig> {
    const explicitLoaderStartConfig: Partial<LoaderStartConfig> = {};
    const mode = httpRequest.context.get(LOADER_MODE_CONTEXT);
    const scope = httpRequest.context.get(LOADER_SCOPE_CONTEXT);
    const groupId = httpRequest.context.get(LOADER_GROUP_CONTEXT);
    const message = httpRequest.context.get(LOADER_MESSAGE_CONTEXT);
    const priority = httpRequest.context.get(LOADER_PRIORITY_CONTEXT);
    const referenceKey = httpRequest.context.get(LOADER_REFERENCE_CONTEXT);
    const routeKey = httpRequest.context.get(LOADER_ROUTE_KEY_CONTEXT);
    const moduleKey = httpRequest.context.get(LOADER_MODULE_KEY_CONTEXT);

    if (mode) {
      explicitLoaderStartConfig.mode = mode;
    }

    if (scope) {
      explicitLoaderStartConfig.scope = scope;
    }

    if (groupId) {
      explicitLoaderStartConfig.groupId = groupId;
    }

    if (message) {
      explicitLoaderStartConfig.message = message;
    }

    if (priority !== null) {
      explicitLoaderStartConfig.priority = priority;
    }

    if (referenceKey) {
      explicitLoaderStartConfig.referenceKey = referenceKey;
    }

    if (routeKey) {
      explicitLoaderStartConfig.routeKey = routeKey;
    }

    if (moduleKey) {
      explicitLoaderStartConfig.moduleKey = moduleKey;
    }

    return explicitLoaderStartConfig;
  }

  private buildContextFlags(
    httpRequest: HttpRequest<unknown>,
    explicitRequestOverrides: Partial<LoaderStartConfig>
  ): Set<string> {
    const contextFlags = new Set<string>();

    if (httpRequest.context.get(LOADER_SKIP_CONTEXT)) {
      contextFlags.add('skip-requested');
    }

    if (explicitRequestOverrides.mode) {
      contextFlags.add('has-explicit-mode');
    }

    if (explicitRequestOverrides.scope) {
      contextFlags.add('has-explicit-scope');
    }

    if (explicitRequestOverrides.groupId) {
      contextFlags.add('has-explicit-group');
    }

    if (explicitRequestOverrides.message) {
      contextFlags.add('has-explicit-message');
    }

    if (explicitRequestOverrides.priority !== undefined) {
      contextFlags.add('has-explicit-priority');
    }

    if (explicitRequestOverrides.referenceKey) {
      contextFlags.add('has-reference-key');
    }

    if (explicitRequestOverrides.routeKey) {
      contextFlags.add('has-route-key');
    }

    if (explicitRequestOverrides.moduleKey) {
      contextFlags.add('has-module-key');
    }

    return contextFlags;
  }

  private buildSkippedPolicy(
    loaderStartConfig: LoaderStartConfig
  ): ResolvedLoaderPolicy {
    const defaultLoaderStartConfig = resolveLoaderStartConfig(loaderStartConfig);

    return {
      skip: true,
      mode: defaultLoaderStartConfig.mode,
      scope: defaultLoaderStartConfig.scope,
      owner: defaultLoaderStartConfig.owner,
      delayMs: defaultLoaderStartConfig.delayMs ?? 0,
      minVisibleMs: defaultLoaderStartConfig.minVisibleMs ?? 0,
      priority: defaultLoaderStartConfig.priority ?? 0,
      routeKey: defaultLoaderStartConfig.routeKey,
      moduleKey: defaultLoaderStartConfig.moduleKey,
      source: defaultLoaderStartConfig.source,
    };
  }

  private hasLoaderBehaviorOverride(
    loaderStartConfig: Partial<LoaderStartConfig> | undefined
  ): boolean {
    if (!loaderStartConfig) {
      return false;
    }

    return [
      loaderStartConfig.mode,
      loaderStartConfig.scope,
      loaderStartConfig.groupId,
      loaderStartConfig.message,
      loaderStartConfig.priority,
      loaderStartConfig.referenceKey,
      loaderStartConfig.delayMs,
      loaderStartConfig.minVisibleMs,
      loaderStartConfig.timeoutMs,
      loaderStartConfig.blocking,
    ].some((configuredValue) => configuredValue !== undefined);
  }

  private getActiveRouteLoaderPolicy(): RouteLoaderPolicyData | undefined {
    return this.extractRouteLoaderPolicy(this.router.routerState.snapshot.root);
  }

  private getPrimaryRouteChain(
    routeSnapshot: ActivatedRouteSnapshot
  ): ActivatedRouteSnapshot[] {
    const routeChain: ActivatedRouteSnapshot[] = [];
    let activeRouteSnapshot: ActivatedRouteSnapshot | null = routeSnapshot;

    while (activeRouteSnapshot) {
      routeChain.push(activeRouteSnapshot);
      activeRouteSnapshot =
        activeRouteSnapshot.children.find(
          (childRouteSnapshot) => childRouteSnapshot.outlet === PRIMARY_OUTLET
        ) ?? activeRouteSnapshot.firstChild ?? null;
    }

    return routeChain;
  }

  private resolveRouteSnapshotKey(
    routeSnapshot?: ActivatedRouteSnapshot | null
  ): string | undefined {
    if (!routeSnapshot) {
      return undefined;
    }

    const resolvedPathSegments = this.getPrimaryRouteChain(routeSnapshot)
      .flatMap((activeRouteSnapshot) => activeRouteSnapshot.url.map((urlSegment) => urlSegment.path))
      .filter(Boolean);

    if (resolvedPathSegments.length === 0) {
      return '/';
    }

    return `/${resolvedPathSegments.join('/')}`;
  }

  private matchesRoutePattern(activeRouteKey: string, routePattern: string): boolean {
    return this.normalizeRouteKey(activeRouteKey).startsWith(
      this.normalizeRouteKey(routePattern)
    );
  }

  private matchesRequestUrlStartsWith(
    comparableRequestUrl: string,
    requestUrlStartsWith: string
  ): boolean {
    const normalizedRequestUrlStartsWith = requestUrlStartsWith.startsWith('/')
      ? requestUrlStartsWith
      : `/${requestUrlStartsWith}`;

    return (
      comparableRequestUrl.startsWith(normalizedRequestUrlStartsWith) ||
      comparableRequestUrl.startsWith(requestUrlStartsWith)
    );
  }

  private isApiRequest(comparableRequestUrl: string): boolean {
    return comparableRequestUrl.startsWith('/api') || comparableRequestUrl.includes('/api/');
  }

  private getComparableRequestUrl(requestUrl: string): string {
    try {
      const parsedUrl = new URL(requestUrl, 'http://placeholder.local');
      return `${parsedUrl.pathname}${parsedUrl.search}`;
    } catch {
      return requestUrl;
    }
  }

  private normalizeRouteKey(routeKey: string): string {
    const [pathWithoutQueryParameters] = routeKey.split('?');
    const [pathWithoutFragment] = pathWithoutQueryParameters.split('#');

    if (!pathWithoutFragment) {
      return '/';
    }

    return pathWithoutFragment.startsWith('/')
      ? pathWithoutFragment
      : `/${pathWithoutFragment}`;
  }
}
